// §4.1's CANDIDATES PANEL — the New customer form's duplicate check (spec 09
// §3.13, §4.1's E2-2c annotation, D-324 rulings 1 to 3; P3-E2 PR E2-2c).
//
// IT WARNS AND NEVER BLOCKS. Nothing here can disable Create: the panel reads
// the check, says what it found, and leaves the decision to the reader. The
// create runs the same check server-side and names `POSSIBLE_DUPLICATE` in its
// 201's `X-Sales-Warning`, which the book states in words (§5's E2-2 row 18).
//
// ITS STATES, each one decided by `panelState` (pure, unit-tested):
//   * idle: nothing worth checking yet (duplicate-check.ts's rule 1). Nothing
//     renders and nothing is reserved.
//   * checking: the first answer for these names is on its way. One line and
//     an inline spinner inside a box that holds the height of one candidate
//     row, so the form does not jump when the answer arrives.
//   * none: no candidate and no hidden match. The panel is hidden.
//   * found: up to five candidates, the exact tax-id match first (the server's
//     order is kept), each with its customer number, the field that matched,
//     and Open, which opens the customer in a new tab so the form keeps what
//     was typed. A later check keeps the last answer on screen with the
//     spinner beside the title, so the list never collapses between pauses.
//   * hidden-only, or found with hidden matches: the sentence "N similar
//     customers you cannot open" (§7 narrowing 20 counts display-name matches
//     on customers the reader's door does not answer, and nothing else).
//   * failed: the check failed or was throttled (429 at 30 a minute). One
//     muted line saying so and that the create is still allowed. A 403 (a
//     reader the narrowing refuses, finance's credit-only grant) renders
//     nothing at all, because a reader who cannot press the check has nothing
//     to be told about it.
//
// ON A PHONE the panel sits under the fields: it is the last block of the
// form's body in source order, and its rows wrap rather than scroll sideways.

import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { StatusChip } from "../../../../../components/status-chip";
import { RequestError } from "../../../../../lib/request";
import { useDebouncedValue } from "../../../../../lib/use-debounced-value";
import {
  useDuplicateCheck,
  type DuplicateCandidate,
  type DuplicateCheckAnswer,
  type DuplicateCheckBody,
} from "../../hooks/duplicate-queries";
import {
  DUPLICATE_CHECK_DEBOUNCE_MS,
  duplicateCheckBody,
  type DuplicateCheckInput,
} from "./duplicate-check";
import "./duplicates.css";

/** What the panel shows, decided from the check's read alone. */
export type PanelState =
  | { readonly kind: "idle" }
  | { readonly kind: "checking" }
  | { readonly kind: "none" }
  | { readonly kind: "failed" }
  | { readonly kind: "refused" }
  | {
      readonly kind: "found";
      readonly candidates: readonly DuplicateCandidate[];
      readonly hiddenCount: number;
      /** A newer check is in flight; the list on screen is the last answer. */
      readonly rechecking: boolean;
    };

export interface CheckReadLike {
  readonly data?: DuplicateCheckAnswer | undefined;
  readonly error?: unknown;
  readonly isFetching?: boolean;
  readonly isPlaceholderData?: boolean;
}

/**
 * The panel's state for one render. `asked` is false while there is nothing
 * worth checking. An error wins over a stale answer: an answer to names the
 * reader has since changed is not shown under the line that says the check
 * for the new names failed.
 */
export function panelState(asked: boolean, read: CheckReadLike): PanelState {
  if (!asked) return { kind: "idle" };
  if (read.error !== null && read.error !== undefined) {
    return read.error instanceof RequestError && read.error.httpStatus === 403
      ? { kind: "refused" }
      : { kind: "failed" };
  }
  const answer = read.data;
  if (answer === undefined) return { kind: "checking" };
  const rechecking = read.isPlaceholderData === true && read.isFetching === true;
  if (answer.candidates.length === 0 && answer.hidden_count === 0) {
    // A stale "nothing" while the next answer is asked keeps the box reserved
    // rather than flashing it open on whatever the new answer says.
    return rechecking ? { kind: "checking" } : { kind: "none" };
  }
  return {
    kind: "found",
    candidates: answer.candidates,
    hiddenCount: answer.hidden_count,
    rechecking,
  };
}

/** The form's half: the typed values, debounced, then checked. */
export function DuplicateCandidatesPanel(input: DuplicateCheckInput) {
  const typed = duplicateCheckBody(input);
  // Debounced on the BODY's identity in text, so a keystroke that does not
  // change what would be asked (a trailing space) does not restart the wait.
  const settledKey = useDebouncedValue(JSON.stringify(typed ?? null), DUPLICATE_CHECK_DEBOUNCE_MS);
  const settled = parseBody(settledKey);
  const read = useDuplicateCheck(settled);
  return <DuplicateCandidatesView state={panelState(settled !== undefined, read)} />;
}

function parseBody(text: string): DuplicateCheckBody | undefined {
  // The text is `duplicateCheckBody`'s own answer, stringified a line above.
  const value = JSON.parse(text) as DuplicateCheckBody | null;
  return value ?? undefined;
}

/** The panel's markup for a state. Exported for the unit test. */
export function DuplicateCandidatesView({ state }: { state: PanelState }) {
  const { t } = useTranslation();

  if (state.kind === "idle" || state.kind === "none" || state.kind === "refused") return null;

  if (state.kind === "checking") {
    return (
      <div
        className="sales-dup-panel is-checking"
        role="status"
        aria-busy="true"
        data-testid="sales-dup-panel"
        data-state="checking"
      >
        <p className="sales-dup-line">
          <span className="sales-dup-spin" aria-hidden="true" />
          {t("sales.duplicates.checking")}
        </p>
      </div>
    );
  }

  if (state.kind === "failed") {
    return (
      <div
        className="sales-dup-panel is-quiet"
        role="status"
        data-testid="sales-dup-panel"
        data-state="failed"
      >
        <p className="sales-dup-line sales-dup-muted" data-testid="sales-dup-failed">
          {t("sales.duplicates.failed")}
        </p>
      </div>
    );
  }

  const { candidates, hiddenCount, rechecking } = state;
  return (
    <section
      className="sales-dup-panel"
      aria-label={t("sales.duplicates.title")}
      aria-busy={rechecking || undefined}
      data-testid="sales-dup-panel"
      data-state="found"
    >
      {candidates.length > 0 ? (
        <>
          <p className="sales-dup-title">
            {t("sales.duplicates.title")}
            {rechecking ? <span className="sales-dup-spin" aria-hidden="true" /> : null}
          </p>
          <ul className="sales-dup-list">
            {candidates.map((candidate) => (
              <li
                key={candidate.id}
                className="sales-dup-row"
                data-testid="sales-dup-candidate"
                data-match={candidate.match}
              >
                <span className="sales-dup-name">
                  <span className="sales-dup-display">{candidate.display_name}</span>
                  {candidate.legal_name !== undefined &&
                  candidate.legal_name !== candidate.display_name ? (
                    <span className="sales-dup-legal">{candidate.legal_name}</span>
                  ) : null}
                </span>
                <span className="sales-dup-meta">
                  <span className="sales-dup-no">{candidate.customer_no}</span>
                  <StatusChip variant={candidate.match === "tax_id" ? "warn" : "neutral"}>
                    {matchLabel(t, candidate.match)}
                  </StatusChip>
                  <Link
                    to="/sales/customers/$customerId"
                    params={{ customerId: candidate.id }}
                    target="_blank"
                    rel="noopener"
                    className="ds-lnk"
                    aria-label={t("sales.duplicates.openLabel", {
                      customer: candidate.display_name,
                    })}
                    data-testid="sales-dup-open"
                  >
                    {t("sales.duplicates.open")}
                  </Link>
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {hiddenCount > 0 ? (
        <p className="sales-dup-line sales-dup-muted" data-testid="sales-dup-hidden">
          {t("sales.duplicates.hidden", { count: hiddenCount })}
          {candidates.length === 0 && rechecking ? (
            <span className="sales-dup-spin" aria-hidden="true" />
          ) : null}
        </p>
      ) : null}
      <p className="sales-dup-line sales-dup-muted">{t("sales.duplicates.hint")}</p>
    </section>
  );
}

function matchLabel(
  t: ReturnType<typeof useTranslation>["t"],
  match: DuplicateCandidate["match"],
): string {
  switch (match) {
    case "tax_id":
      return t("sales.duplicates.matchTaxId");
    case "display_name":
      return t("sales.duplicates.matchDisplayName");
    case "legal_name":
      return t("sales.duplicates.matchLegalName");
  }
}
